import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cbcQuarantineSession } from '../lib/cbcQuarantineSession';
import {
  isCbcPackageNewerThanBaseline,
  uploadedDocumentIdentity,
} from '../lib/cbcUploadLifecycle';
import type { CbcPackage } from '../lib/cbcTranslation';

export interface PendingCbcPackage {
  documentIdentity: string;
  /** Fingerprint of the package that existed *before* this upload. */
  baselineFingerprint: string | null | undefined;
  submittedAt: number;
  lockActive: boolean;
}

export interface UseCbcQuarantineOptions {
  appId: string | number;
  stepKey: string;
  /** Hub packages from useCbcStatus — used to release quarantine on new fingerprints. */
  hubPackages: CbcPackage[];
  /** Called when the session cache write fails. Distinct from auto-submit failures. */
  onPersistError: (notice: string) => void;
}

export interface UseCbcQuarantineResult {
  /** Packages after quarantine filtering — old package hidden until Hub reports new fingerprint. */
  visiblePackages: CbcPackage[];
  /** Current quarantine map (read-only for consumers). */
  pendingPackages: Record<string, PendingCbcPackage>;
  /** Seed identities + restore stored quarantine. Call once when merged form values are known. */
  hydrate: (
    mergedValues: Record<string, unknown>,
    cbcFieldKeys: string[],
    storedQuarantine: Record<string, PendingCbcPackage> | undefined,
  ) => void;
  /** Add or replace a pending entry for a field. */
  addPending: (fieldKey: string, entry: PendingCbcPackage) => void;
  /** Patch the baseline fingerprint for an in-flight entry (post-refetch). */
  updateBaseline: (fieldKey: string, documentIdentity: string, fingerprint: string | null) => void;
  /** Release the optimistic lock for a failed auto-submit (keeps quarantine intact). */
  releaseLock: (fieldKey: string, documentIdentity: string) => void;
  /** Remove a pending entry entirely (file removed by applicant). */
  removePending: (fieldKey: string) => void;
  /** Refresh submittedAt for all active entries after a successful submit. */
  onSubmissionSucceeded: () => void;
}

export const CBC_QUARANTINE_STORAGE_NOTICE =
  'We could not save the background-check processing state on this device. ' +
  'Your uploaded file is still attached; keep this page open and do not replace it. ' +
  'Contact our team if you need help continuing.';

/** Owns CBC replacement attribution and its ordered session-cache writes. */
export function useCbcQuarantine({
  appId,
  stepKey,
  hubPackages,
  onPersistError,
}: UseCbcQuarantineOptions): UseCbcQuarantineResult {
  const [pendingPackages, setPendingPackages] = useState<Record<string, PendingCbcPackage>>({});
  const hydratedRef = useRef(false);

  // In-order queue prevents concurrent cache writes from racing each other.
  const persistQueueRef = useRef<Promise<unknown>>(Promise.resolve());

  // ── Quarantine persist effect ──────────────────────────────────────────────
  useEffect(() => {
    if (!hydratedRef.current) return;
    const snapshot = pendingPackages;
    persistQueueRef.current = persistQueueRef.current.then(async () => {
      try {
        if (Object.keys(snapshot).length > 0) {
          await cbcQuarantineSession.save(appId, stepKey, snapshot);
        } else {
          await cbcQuarantineSession.clear(appId, stepKey);
        }
      } catch (err) {
        console.error('[useCbcQuarantine] persist failed:', err);
        onPersistError(CBC_QUARANTINE_STORAGE_NOTICE);
      }
    });
  }, [appId, stepKey, pendingPackages, onPersistError]);

  // ── Release quarantine when Hub reports a new fingerprint ─────────────────
  useEffect(() => {
    setPendingPackages((current) => {
      let changed = false;
      const next = { ...current };
      for (const [fieldKey, pending] of Object.entries(current)) {
        const pkg = hubPackages.find(
          (p) => p.stepKey === stepKey && p.fieldKey === fieldKey,
        );
        if (isCbcPackageNewerThanBaseline(pkg, pending.baselineFingerprint)) {
          delete next[fieldKey];
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [hubPackages, stepKey]);

  // ── Visible packages (quarantine filter) ──────────────────────────────────
  const visiblePackages = useMemo(
    () =>
      hubPackages.filter((pkg) => {
        const pending = pkg.stepKey === stepKey ? pendingPackages[pkg.fieldKey] : undefined;
        return !pending || isCbcPackageNewerThanBaseline(pkg, pending.baselineFingerprint);
      }),
    [hubPackages, pendingPackages, stepKey],
  );

  // ── Public API ─────────────────────────────────────────────────────────────

  const hydrate = useCallback(
    (
      mergedValues: Record<string, unknown>,
      cbcFieldKeys: string[],
      storedQuarantine: Record<string, PendingCbcPackage> | undefined,
    ) => {
      const restored: Record<string, PendingCbcPackage> = {};
      for (const [fieldKey, marker] of Object.entries(storedQuarantine ?? {})) {
        const currentIdentity = uploadedDocumentIdentity(mergedValues[fieldKey]);
        const currentPackage = hubPackages.find(
          (pkg) => pkg.stepKey === stepKey && pkg.fieldKey === fieldKey,
        );
        if (
          cbcFieldKeys.includes(fieldKey) &&
          currentIdentity &&
          currentIdentity === marker.documentIdentity &&
          Number.isFinite(marker.submittedAt) &&
          !isCbcPackageNewerThanBaseline(currentPackage, marker.baselineFingerprint)
        ) {
          restored[fieldKey] = marker;
        }
      }
      setPendingPackages(restored);
      hydratedRef.current = true;
    },
    [hubPackages, stepKey],
  );

  const addPending = useCallback((fieldKey: string, entry: PendingCbcPackage) => {
    setPendingPackages((prev) => ({ ...prev, [fieldKey]: entry }));
  }, []);

  const updateBaseline = useCallback(
    (fieldKey: string, documentIdentity: string, fingerprint: string | null) => {
      setPendingPackages((prev) => {
        if (prev[fieldKey]?.documentIdentity !== documentIdentity) return prev;
        return { ...prev, [fieldKey]: { ...prev[fieldKey], baselineFingerprint: fingerprint } };
      });
    },
    [],
  );

  const releaseLock = useCallback((fieldKey: string, documentIdentity: string) => {
    setPendingPackages((prev) => {
      if (prev[fieldKey]?.documentIdentity !== documentIdentity) return prev;
      return { ...prev, [fieldKey]: { ...prev[fieldKey], lockActive: false } };
    });
  }, []);

  const removePending = useCallback((fieldKey: string) => {
    setPendingPackages((prev) => {
      if (!prev[fieldKey]) return prev;
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  }, []);

  const onSubmissionSucceeded = useCallback(() => {
    setPendingPackages((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const submittedAt = Date.now();
      return Object.fromEntries(
        Object.entries(prev).map(([k, v]) => [k, { ...v, submittedAt, lockActive: true }]),
      );
    });
  }, []);

  return {
    visiblePackages,
    pendingPackages,
    hydrate,
    addPending,
    updateBaseline,
    releaseLock,
    removePending,
    onSubmissionSucceeded,
  };
}
